import React from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { ITransaction } from '@/types'
import { formatAmount, formatDayMonthYear } from '@/utils/common'
import { aliasLabel, DescriptionAlias } from './aliasOptions'

interface ReviewAliasDialogProps {
  open: boolean
  rows: ITransaction[]
  alias: DescriptionAlias
  isApplying: boolean
  progress: { done: number; total: number }
  // Set after a run stopped part way; rows then holds only what is still to do.
  failure?: { message: string; updatedCount: number }
  onCancel: () => void
  onConfirm: () => void
}

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`

// Shows exactly what an alias update will change before anything is sent, so a wrong pick is caught
// here rather than after the bank service has stored it.
export const ReviewAliasDialog: React.FC<ReviewAliasDialogProps> = ({
  open,
  rows,
  alias,
  isApplying,
  progress,
  failure,
  onCancel,
  onConfirm,
}) => {
  const accountCount = new Set(rows.map(row => row.accountId)).size
  const unchanged = rows.filter(row => row.descriptionAlias === alias).length
  const replacing = rows.filter(
    row => row.descriptionAlias && row.descriptionAlias !== alias
  ).length

  return (
    <Dialog
      open={open}
      // Closing mid-run would hide a failure the user needs to see, so the dialog stays until done.
      onClose={isApplying ? undefined : onCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="review-alias-title"
      sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
    >
      <DialogTitle id="review-alias-title" sx={{ fontSize: '18px', fontWeight: 600 }}>
        Review description alias update
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: '14px', mb: 1 }}>
          Set the description alias to <strong>{aliasLabel(alias)}</strong> on{' '}
          {plural(rows.length, 'transaction')} across {plural(accountCount, 'account')}.
        </Typography>

        {(replacing > 0 || unchanged > 0) && (
          <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5, fontSize: '14px' }}>
            {replacing > 0 && (
              <li>{plural(replacing, 'transaction')} will have a different alias replaced.</li>
            )}
            {unchanged > 0 && (
              <li>
                {plural(unchanged, 'transaction')} already {unchanged === 1 ? 'has' : 'have'} this
                alias.
              </li>
            )}
          </Box>
        )}

        {failure && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {failure.updatedCount > 0 &&
              `${plural(failure.updatedCount, 'transaction')} updated before the error. `}
            The update stopped: {failure.message} The {plural(rows.length, 'transaction')} below may
            or may not have been updated, so they are shown as last read. Trying again is safe.
          </Alert>
        )}

        <TableContainer sx={{ maxHeight: 360, border: '1px solid #EAECF0', borderRadius: '8px' }}>
          <Table size="small" stickyHeader aria-label="Transactions to update">
            <TableHead>
              <TableRow>
                <HeadCell>Date</HeadCell>
                <HeadCell>Description</HeadCell>
                <HeadCell align="right">Amount</HeadCell>
                <HeadCell>Account</HeadCell>
                <HeadCell>Current alias</HeadCell>
                <HeadCell>New alias</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.transactionId}>
                  <BodyCell>{formatDayMonthYear(row.transactionDate)}</BodyCell>
                  <BodyCell>{row.description}</BodyCell>
                  <BodyCell align="right">{formatAmount(row.amount)}</BodyCell>
                  <BodyCell>{[row.name, row.accountNumber].filter(Boolean).join(' ')}</BodyCell>
                  <BodyCell>{aliasLabel(row.descriptionAlias) || '—'}</BodyCell>
                  <BodyCell>{aliasLabel(alias)}</BodyCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {isApplying && (
          <Box mt={2}>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
            />
            <Typography sx={{ fontSize: '13px', mt: 0.5, color: 'text.secondary' }}>
              Updating… {progress.done} of {progress.total}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isApplying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isApplying || rows.length === 0}>
          {failure ? 'Try again' : `Update ${plural(rows.length, 'transaction')}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const HeadCell = styled(TableCell)({
  fontSize: '13px',
  fontWeight: 600,
  backgroundColor: '#F9FAFB',
  whiteSpace: 'nowrap',
})

const BodyCell = styled(TableCell)({
  fontSize: '13px',
})
